/**
 * The business's brand colour on our hosted pages: which text reads on it, and how far the
 * colour itself has to move before any text does. Pure, so the shell and Brand settings agree.
 */

export type PublicThemeTone = 'light' | 'dark';

export interface Oklch { l: number; c: number; h: number }

export interface ResolvedBrandColor {
  /** The colour to paint, as #rrggbb. Equals the input unless it had to be stepped. */
  brand: string;
  /** White or ink, whichever has the higher contrast against `brand`. */
  onBrand: string;
  onBrandTone: 'white' | 'ink';
  contrast: number;
  adjusted: boolean;
}

/** Unitfield action blue, used when the business has not chosen a colour. */
export const DEFAULT_BRAND_OKLCH: Oklch = { l: 0.56, c: 0.215, h: 261 };
export const BRAND_WHITE = '#ffffff';
export const MIN_BRAND_CONTRAST = 4.5;

const HEX_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function normalizeHex(value: string | null | undefined): string | null {
  const match = value?.trim().match(HEX_PATTERN)?.[1];
  if (!match) return null;
  const full = match.length === 3 ? match.replace(/./g, (digit) => digit + digit) : match;
  return `#${full.toLowerCase()}`;
}

function hexToRgb(hex: string): [number, number, number] {
  const full = normalizeHex(hex);
  if (!full) throw new Error(`Not a hex colour: ${hex}`);
  return [1, 3, 5].map((offset) => parseInt(full.slice(offset, offset + 2), 16) / 255) as [number, number, number];
}

const toLinear = (value: number) => (value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);
const fromLinear = (value: number) => (value <= 0.0031308 ? value * 12.92 : 1.055 * value ** (1 / 2.4) - 0.055);

export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(toLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const [light, dark] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (light + 0.05) / (dark + 0.05);
}

export function hexToOklch(hex: string): Oklch {
  const [r, g, b] = hexToRgb(hex).map(toLinear);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const hue = (Math.atan2(B, A) * 180) / Math.PI;
  return { l: L, c: Math.hypot(A, B), h: hue < 0 ? hue + 360 : hue };
}

function oklchToLinearRgb({ l: L, c, h }: Oklch): [number, number, number] {
  const A = c * Math.cos((h * Math.PI) / 180);
  const B = c * Math.sin((h * Math.PI) / 180);
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

const inGamut = (rgb: number[]) => rgb.every((channel) => channel >= -0.0001 && channel <= 1.0001);

/** Converts to #rrggbb, lowering chroma at the same lightness and hue when the colour is outside sRGB. */
export function oklchToHex(color: Oklch): string {
  let { c } = color;
  let rgb = oklchToLinearRgb({ ...color, c });
  while (!inGamut(rgb) && c > 0) {
    c = Math.max(0, c - 0.002);
    rgb = oklchToLinearRgb({ ...color, c });
  }
  return `#${rgb
    .map((channel) => Math.round(Math.min(1, Math.max(0, fromLinear(Math.min(1, Math.max(0, channel))))) * 255))
    .map((channel) => channel.toString(16).padStart(2, '0'))
    .join('')}`;
}

/** The shell's light-theme ink, oklch(19% 0.004 85). */
export const BRAND_INK = oklchToHex({ l: 0.19, c: 0.004, h: 85 });

function bestText(brand: string) {
  const white = contrastRatio(brand, BRAND_WHITE);
  const ink = contrastRatio(brand, BRAND_INK);
  return white >= ink
    ? { onBrand: BRAND_WHITE, onBrandTone: 'white' as const, contrast: white }
    : { onBrand: BRAND_INK, onBrandTone: 'ink' as const, contrast: ink };
}

/**
 * Picks the text for the brand colour. When neither white nor ink reaches 4.5:1, the colour
 * steps darker in OKLCH lightness on a light page, lighter on a dark one, until one does.
 */
export function resolveBrandColor(primaryColor: string | null | undefined, tone: PublicThemeTone = 'light'): ResolvedBrandColor {
  const input = normalizeHex(primaryColor) ?? oklchToHex(DEFAULT_BRAND_OKLCH);
  const first = bestText(input);
  if (first.contrast >= MIN_BRAND_CONTRAST) return { brand: input, ...first, adjusted: false };

  const start = hexToOklch(input);
  const direction = tone === 'dark' ? 1 : -1;
  for (let step = 1; step <= 100; step += 1) {
    const l = Math.min(1, Math.max(0, start.l + direction * step * 0.01));
    const brand = oklchToHex({ ...start, l });
    const text = bestText(brand);
    if (text.contrast >= MIN_BRAND_CONTRAST) return { brand, ...text, adjusted: true };
  }
  // Unreachable for real colours: pure black and pure white both pass.
  return { brand: input, ...first, adjusted: false };
}

/** The settings screen's sentence under the sample pill. */
export function brandTextAdvice(primaryColor: string | null | undefined): string {
  const resolved = resolveBrandColor(primaryColor, 'light');
  return resolved.onBrandTone === 'white'
    ? 'White text reads well on this color'
    : 'Dark text reads well on this color';
}
